import { useMutation, useQuery } from '@apollo/client';
import { Button, message } from 'antd';
import OrderListingQueryHint from '../../../../components/filters/OrderListingQueryHint';
import { SiteType } from 'graphql/types/SiteType';
import { PDA_ONLY_EXCEPTION_CODES } from 'config/roleExceptionConfig';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import SetReturnStatusModal, { SetReturnStatusData } from "../../../../components/SetReturnStatusModal";
import SetDeliverStatusModal from "../../../../components/SetDeliverStatusModal";
import FailedOrdersModal from "../../../../components/FailedOrdersModal";
import {
    hasExceptionOptions,
    isComplaintOptions,
    overallStatusOptions,
    podReviewStatusOptions
} from "../../../../config/filterOptions";
import { MARK_AS_EXCEPTION } from '../../../../graphql/mutations/markAsException';
import { SET_RETURNED_STATUS } from "../../../../graphql/mutations/setReturnedStatus";
import { BATCH_MARK_FOR_MANUAL_REVIEW, BatchMarkForManualReviewResponse, BatchMarkForManualReviewVariables } from '../../../central/parcels/graphql/mutations/batchMarkForManualReviewMutations';
import AddToPodReviewModal from '../../../central/parcels/components/AddToPodReviewModal';
import { useSetDeliverStatus } from "../../../../hooks/useSetDeliverStatus";
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../../graphql/queries/getChannelAll';
import {
    GET_NEXT_ACTION_LIST,
    NextActionListResponse,
    NextActionListType
} from "../../../../graphql/queries/getNextActionList";
import { OrderType } from "../../../../graphql/types/OrderType";
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { getUserInfo } from "../../../../utils/auth";
import { hasResource } from "../../../../utils/resourceAuth";
import { ResourceCode } from "../../../../constants/resourceCodes";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import DateTimeRangeFilterItem from "../../../delivery/components/DateTimeRangeFilterItem";
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import {
    GENERATE_BATCH_SHIPPING_LABEL_PDF,
    GenerateBatchShippingLabelPdfResponse,
    GenerateBatchShippingLabelPdfVariables
} from "../../../delivery/parcels/graphql/mutations/generateShippingLabelPdf";
import {
    ExceptionDictionaryResponse,
    GET_ALL_EXCEPTION,
    ExceptionDictionaryType
} from "../../../delivery/parcels/graphql/queries/getAllException";
import {
    GetHubListForOptionResponse,
    GET_HUB_LIST_FOR_OPTIONS
} from "../../../../graphql/queries/warehouseQueries";
import { printPdfFromBase64 } from "../../../wms/bags/utils/printUtils";
import { WMSOrderFiltersType } from '../graphql/type/WMSOrderFiltersType';
import BagTaskNumberFilterItem from "./BagTaskNumberFilterItem";
import BatchMarkAsExceptionModal, { BatchMarkExceptionInput } from 'components/BatchMarkAsExceptionModal';
import { useDspOptions } from '../../../../hooks/useDspOptions';
import { HubSelectFilter } from '../../../../components/filters/HubSelectFilter';
import { WarehouseSelectFilter } from '../../../../components/filters/WarehouseSelectFilter';
import { GET_HUB_LIST, GetHubListResponse } from '../../../wms/bags/graphql/queries/getHubList';
import { DspSelectFilter } from '../../../../components/filters/DspSelectFilter';
import { MultiClientSelectFilter } from '../../../../components/filters/MultiClientSelectFilter';
import { MultiPickupHubSelectFilter } from '../../../../components/filters/MultiPickupHubSelectFilter';
import { PrePickupHubSelectFilter } from '../../../../components/filters/PrePickupHubSelectFilter';
import { MultiParcelStatusSelectFilter } from '../../../../components/filters/MultiParcelStatusSelectFilter';

import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';
import { GENERATE_PARCEL_DOWNLOAD_REPORT_FOR_WMS, GenerateParcelDownloadReportForWMSResponse, GenerateParcelDownloadReportForWMSVariables } from '../graphql/mutations/generateParcelDownloadReport';
import ParcelsDownloadModal from './ParcelsDownloadModal';
import { getLastWeekDateTimeRange } from '../../../../utils/dateTimeUtils';
import { buildParcelQueryFilters, normalizeRequiredFields } from '../utils/buildParcelQueryFilters';
import { parseException, showError } from '../../../../utils/exceptionUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
import {
    DeliveredAtRangeField,
    isDeliveredAtOutOfRange,
    useParcelQueryRangeDays,
} from '../../../../components/parcelQueryRange';



type WmsParcelFilters = WMSOrderFiltersType & {
    // main 合并后新增字段：允许当前 Hub 退件到达时间筛选
    returnArrivalHubAt?: [string | null, string | null] | null;
};

interface WmsParcelFilterProps {
    filters: WmsParcelFilters;
    setFilters: (filters: WmsParcelFilters | ((prev: WmsParcelFilters) => WmsParcelFilters)) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: OrderType[];
    onClearSelection?: () => void; // 添加清空选择的回调函数
}

interface CustomerOption {
    value: string;
    label: string;
}

const WmsParcelFilter: React.FC<WmsParcelFilterProps> = ({
                                                       filters,
                                                       setFilters,
                                                       handleFilter,
                                                       handleResetFilters,
                                                       selectedRows,
                                                       onClearSelection,
                                                   }) => {
    const { t, i18n } = useTranslation();
    // 签收时间开始、结束都超出「妥投后 N 天」时禁止查询
    const queryRangeDays = useParcelQueryRangeDays();
    const deliveredAtBlocked = isDeliveredAtOutOfRange(filters.deliveredAt, queryRangeDays);
    const guardedHandleFilter = () => {
        if (deliveredAtBlocked) return;
        handleFilter();
    };
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
    const isSpanish = currentLanguage === 'es';

    // ===== Label constants (for column alignment) =====
    const labelTracking = String(t('wms.parcels.filters.tracking'));
    const labelOrder = String(t('wms.parcels.filters.order'));
    const labelExternalTracking = String(t('wms.parcels.filters.externalTrackingNumber'));
    const labelClient = String(t('wms.parcels.filters.client'));
    const labelParcelStatus = String(t('wms.parcels.filters.parcelStatus'));
    const labelDsp = 'DSP';

    const labelFirstSortCode = String(t('wms.parcels.filters.firstSortCode'));
    const labelSecondSortCode = String(t('wms.parcels.filters.secondSortCode'));
    const labelThirdSortCode = String(t('wms.parcels.filters.thirdSortCode'));
    const labelReceiverZipcode = String(t('wms.parcels.filters.receiverZipcode'));
    const labelBagTaskNumber = String(t('wms.parcels.filters.bagTaskNumber'));
    const labelSortingChannelOrderNumber = String(t('wms.parcels.filters.sortingChannelOrderNumber'));
    const labelPickupHub = String(t('delivery.parcels.label.pickupHub'));
    const labelPickupSite = String(t('wms.parcels.filters.pickupSite'));
    const labelPrePickupHub = String(t('central.parcels.filters.prePickupHub'));

    const labelOrderTime = String(t('wms.parcels.filters.orderTime'));
    const labelPickupTaskDate = String(t('wms.parcels.filters.pickupTaskDate'));
    const labelDeliveredAt = String(t('wms.parcels.filters.deliveredAt'));
    const labelHubReceiveDate = String(t('wms.parcels.filters.hubReceiveDate'));
    const labelWhsReceiveDate = String(t('wms.parcels.filters.whsReceiveDate'));
    const labelLastDeliveryScanTime = String(t('wms.parcels.filters.lastDeliveryScanTime'));
    const labelLastTaskDate = String(t('wms.parcels.filters.lastTaskDate'));
    const labelLastUpdatedAt = String(t('delivery.parcels.label.lastUpdatedAt'));
    const labelReturnArrivalHubAt = String(t('wms.parcels.filters.returnArrivalHubAt'));

    // 默认时间范围不写入 URL：用于 UI 默认展示与下载参数构造
    // - undefined：代表“未初始化”，默认展示最近一周
    // - null：代表用户手动清空（点击叉号），必须保持为空，不再回填默认值
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedOrderTimeRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters: (next) => setFilters(next),
        timeKey: 'orderTime',
        hasUserEditedTimeRef: hasUserEditedOrderTimeRef,
        // wms/parcels 会在 mount 后通过 useEffect 自动补 currentWarehouseId/sortingCode 等非时间筛选，
        // 避免因此把默认时间立刻清空（表现为默认时间一闪而过）
        skipFirstNonTimeChange: true,
        // 切换仓库时 currentWarehouseId/sortingCode 会被 useEffect 自动同步，
        // 这类非用户操作的变更不应触发默认时间清空
        excludeKeys: ['currentWarehouseId', 'sortingCode'],
    });

    const getEffectiveOrderTime = useCallback((
        orderTime: WMSOrderFiltersType['orderTime']
    ): [string | null, string | null] | null => {
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2;
        return isValid ? (orderTime as [string | null, string | null]) : getLastWeekDateTimeRange();
    }, []);

    const labelIsComplaint = String(t('wms.parcels.filters.isComplaint'));
    const labelPodReviewStatus = String(t('delivery.parcels.label.podReviewStatus'));
    const labelHasException = String(t('wms.parcels.filters.hasException'));
    const labelLastExceptionReason = String(t('wms.parcels.filters.lastExceptionReason'));
    const labelLastExceptionTime = String(t('wms.parcels.filters.lastExceptionDate'));
    const labelNextAction = String(t('wms.parcels.filters.nextAction'));
    const labelExceptionStatus = String(t('wms.parcels.filters.exceptionStatus'));

    // ===== Cross-block 3-column alignment =====
    const col1Labels = [
        labelTracking,
        labelOrder,
        labelDsp,
        labelFirstSortCode,
        labelReceiverZipcode,
        labelPrePickupHub,
        labelIsComplaint,
        labelLastExceptionReason,
        labelExceptionStatus,
    ];
    const col2Labels = [
        labelExternalTracking,
        labelClient,
        labelSecondSortCode,
        labelBagTaskNumber,
        labelPickupSite,
        labelPodReviewStatus,
    ];
    const col3Labels = [
        labelParcelStatus,
        labelThirdSortCode,
        labelPickupHub,
        labelHasException,
        labelNextAction,
        labelSortingChannelOrderNumber,
    ];

    // Date Information 区域为二分布局（两列）
    const dateCol1Labels = [
        labelOrderTime,
        labelDeliveredAt,
        labelWhsReceiveDate,
        labelLastTaskDate,
        labelReturnArrivalHubAt,
        labelLastExceptionTime,
    ];
    const dateCol2Labels = [
        labelPickupTaskDate,
        labelHubReceiveDate,
        labelLastDeliveryScanTime,
        labelLastUpdatedAt,
    ];
    // 二分布局的第一列需要与三分布局的第一列对齐：共享同一个 label 宽度计算结果
    const col1SpanClassName = getSmartLabelClassName([...col1Labels, ...dateCol1Labels]);
    const [selectedHubId, setSelectedHubId] = useState<string | undefined>(undefined);
    const { data: hubListData } = useQuery<GetHubListResponse>(GET_HUB_LIST);
    const [batchModalVisible, setBatchModalVisible] = useState(false);
    const [batchMarkLoading, setBatchMarkLoading] = useState(false);
    const [returnStatusVisible, setReturnStatusVisible] = useState(false);
    const [returnStatusLoading, setReturnStatusLoading] = useState(false);
    const [addToPodReviewModalVisible, setAddToPodReviewModalVisible] = useState(false);
    const [addToPodReviewResult, setAddToPodReviewResult] = useState<BatchMarkForManualReviewResponse | null>(null);
    const [addToPodReviewLoading, setAddToPodReviewLoading] = useState(false);
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);
    const [setReturnedStatus] = useMutation(SET_RETURNED_STATUS);
    const [generateBatchPdf] = useMutation<GenerateBatchShippingLabelPdfResponse, GenerateBatchShippingLabelPdfVariables>(GENERATE_BATCH_SHIPPING_LABEL_PDF);
    const [batchMarkForManualReview] = useMutation<BatchMarkForManualReviewResponse, BatchMarkForManualReviewVariables>(BATCH_MARK_FOR_MANUAL_REVIEW);
    const userRole = getUserInfo()?.account?.role?.name || '';

    const hasPodUpdatePermission = hasResource([ResourceCode.menu.HUB_PACKAGE_SET_DELIVERY_STATUS, ResourceCode.menu.WMS_PACKAGE_SET_DELIVERY_STATUS]);

    const [channelOptions, setChannelOptions] = useState<CustomerOption[]>([]);
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const [hubOptions, setHubOptions] = useState<CustomerOption[]>([]);
    const {data:hubOptionsData} = useQuery<GetHubListForOptionResponse>(GET_HUB_LIST_FOR_OPTIONS);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const [nextActionSelectData, setNextActionSelectData] = useState<NextActionListType[]>([]);
    const {data:nextActionList} = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST);

    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;
    const sortingCodes = currentWarehouse?.sortingCode?.split('-') || [];
    const { dspOptions, loading: dspLoading } = useDspOptions();
    const warehouseId = currentWarehouse?.id ? currentWarehouse?.id.toString() : null;

    // 使用公共hook
    const {
        deliverStatusVisible,
        deliverStatusLoading,
        filteredDeliverRows,
        handleSetDeliverStatus,
        handleSetDeliverStatusConfirm,
        setDeliverStatusVisible,
        failedOrdersVisible,
        failedOrders,
        setFailedOrdersVisible,
        setFailedOrders
    } = useSetDeliverStatus({
        warehouseId: warehouseId,
        handleFilter,
        onClearSelection
    });

    const exceptionSelectData: ExceptionDictionaryType[] = (() => {
        if (!exceptionList.length) return [];
        if (siteType === SiteType.HUB) {
            return exceptionList.filter(item =>
                item.exceptionReasonCode?.startsWith('ERHUB') &&
                !PDA_ONLY_EXCEPTION_CODES.includes(item.exceptionReasonCode)
            );
        }
        if (siteType === SiteType.WAREHOUSE) {
            const blockedCodes = ['ERWHS001', 'ERWHS003', 'ERWHS004', 'ERWHS005'];
            return exceptionList.filter(item =>
                item.exceptionReasonCode?.startsWith('ERWHS') &&
                !blockedCodes.includes(item.exceptionReasonCode!)
            );
        }
        return exceptionList.filter(item => !PDA_ONLY_EXCEPTION_CODES.includes(item.exceptionReasonCode ?? ''));
    })();
    const { getExceptionReasonText, getNextActionTextFromBackend, exceptionOrderStatusOptions } = useStatusInternationalization();
    
    // 下载报表相关状态
    const [generateReportMutation] = useMutation<GenerateParcelDownloadReportForWMSResponse, GenerateParcelDownloadReportForWMSVariables>(GENERATE_PARCEL_DOWNLOAD_REPORT_FOR_WMS);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');

    // 从localStorage获取上次下载时间，页面刷新后仍然有效
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('wmsParcelDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('wmsParcelDownloadLastTime', time.toString());
    };

    // 优化的onChange函数，避免不必要的重新渲染
    // 传递 warehouseId，当清空时同时清空 sortingCodeSegment2（避免状态冲突）
    const handleWarehouseIdChange = useCallback((value: string | undefined) => {
        if (!value) {
            // 清空时，同时清空 sortingCodeSegment2 和 warehouseId
            // 必须明确设置为 undefined，这样 updateHashState 会包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
            setFilters(prev => ({
                ...prev,
                warehouseId: undefined,
                sortingCodeSegment2: undefined
            }));
        } else {
            // 选择时，只更新 warehouseId，sortingCodeSegment2 由 handleSecondSegmentChange 更新
            setFilters(prev => ({ 
                ...prev, 
                warehouseId: value
            }));
        }
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的Sorting-name
    const handleSortingNameChange = useCallback((sortingName: string | undefined) => {
        // 这里可以根据需要处理Sorting-name，比如存储到filters中或进行其他操作
        // 如果需要将Sorting-name存储到filters中，可以取消下面的注释
        // setFilters(prev => ({ ...prev, sortingName: sortingName }));
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的第二段码
    // 注意：清空时不会调用此回调（由 handleWarehouseIdChange 处理），所以这里只处理选择操作
    const handleSecondSegmentChange = useCallback((secondSegment: string | undefined) => {
        // 只在选择时更新 sortingCodeSegment2，清空时由 handleWarehouseIdChange 统一处理
        if (secondSegment) {
            setFilters(prev => ({ ...prev, sortingCodeSegment2: secondSegment }));
        }
    }, [setFilters]);

    useEffect(() => {
        if (currentWarehouse) {
            const needsUpdate = (
                (currentWarehouse?.id && filters.currentWarehouseId !== currentWarehouse.id) ||
                (currentWarehouse?.sortingCode && filters.sortingCode !== currentWarehouse.sortingCode)
            );
            
            if (needsUpdate) {
                const updatedFilters = {...filters};
                updatedFilters.currentWarehouseId = currentWarehouse?.id;
                updatedFilters.sortingCode = currentWarehouse?.sortingCode || '';
                setFilters(updatedFilters);
            }
        }
    }, [currentWarehouse, filters]);

    // 监听重置操作：当第一分拣码和仓库ID都被清空时，清空 selectedHubId
    useEffect(() => {
        if (!filters.sortingCodeSegment1 && !filters.warehouseId) {
            setSelectedHubId(undefined);
        }
    }, [filters.sortingCodeSegment1, filters.warehouseId]);

    // 刷新后恢复 selectedHubId（从 sortingCodeSegment1 反向查找 Hub ID）
    useEffect(() => {
        if (filters.sortingCodeSegment1 && !selectedHubId && hubListData?.hubList) {
            // 根据 sortingCodeSegment1 查找对应的 Hub
            const matchedHub = hubListData.hubList.find(hub => {
                const firstSegment = hub.sortingCode?.split('-')[0];
                return firstSegment === filters.sortingCodeSegment1;
            });
            
            if (matchedHub) {
                setSelectedHubId(matchedHub.id);
            }
        }
    }, [filters.sortingCodeSegment1, selectedHubId, hubListData]);


    useEffect(() => {
        if (data?.getAllChannels) {
            const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
            const options = channels.map(channel => ({
                value: channel.id,
                label: channel.channelName
            }));
            setChannelOptions(options);
        }
        if (hubOptionsData) {
            const hubs = Array.isArray(hubOptionsData.allOptions) ? hubOptionsData.allOptions : [hubOptionsData.allOptions];
            setHubOptions(hubs);
        }
        if (nextActionList?.getNextActionList){
            setNextActionSelectData(nextActionList.getNextActionList);
        }
    }, [data,nextActionList,hubOptionsData]);

    // 下载冷却时间倒计时
    useEffect(() => {
        const interval = setInterval(() => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const currentTime = Date.now();
                const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);

                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem('wmsParcelDownloadLastTime'); // 清理过期数据
                }
            } else {
                setCooldownRemaining(0);
                setDownloadLoading(false);
            }
        }, 100); // 每100ms检查一次，提供流畅的倒计时

        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);

    // 处理时间变化的函数
    const handleOrderTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedOrderTimeRef.current = true;
        setFilters({...filters, orderTime: dates});
    };

    const handleResetFiltersWithDefaultTime = () => {
        hasUserEditedOrderTimeRef.current = false;
        handleResetFilters();
    };

    const handleHubReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destHubArrivedAt: dates});
    };
    
    const handleWarehouseReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destWarehouseArrivedAt: dates});
    };
    
    const handleDeliverTaskChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliverAt: dates});
    };
    
    const handlePickupAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, receivedAtHub: dates});
    };

    const handleExceptionTimeChange = (
        dates: [string | null, string | null] | null,
    ) => {
        setFilters({...filters, markExceptionAt: dates});
    };

    const handleDeliveredAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliveredAt: dates});
    };

    const handleLastDeliveryScanTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, lastDeliveryScanTime: dates});
    };

    const handleUpdatedAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, updatedAt: dates});
    };

    const handleReturnArrivalHubAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, returnArrivalHubAt: dates});
    };

    // 复制选中行的运单号到剪贴板
    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRows || selectedRows.length === 0) {
            message.warning(t('wms.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const trackingNumbers = selectedRows
            .map(row => row.trackingNumber?.trim())
            .filter((tracking): tracking is string => Boolean(tracking && tracking.length > 0));
        if (trackingNumbers.length === 0) {
            message.warning(t('wms.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (error) {
            console.error('Copy tracking numbers failed:', error);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRows, t]);

    const handleMarkAsException = () => {
        if (selectedRows.length < 1) {
            message.warning(t('wms.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        setBatchModalVisible(true);
    };

    const handlePrintLabel = async () => {
        if (selectedRows.length < 1) {
            message.warning(t('wms.parcels.messages.selectAtLeastOnePackage'));
            return;
        }

        try {
            // 批量生成一个包含多页的PDF
            const orderIds = selectedRows.map(order => parseInt(order.id));
            const { data } = await generateBatchPdf({
                variables: { orderIds }
            });

            if (data?.generateBatchShippingLabelPdf) {
                // 生成包含所有选中订单号的文件名
                const trackingNumbers = selectedRows
                    .map(order => order.trackingNumber || order.id)
                    .slice(0, 3); // 只取前3个，避免文件名过长
                const filenameSuffix = trackingNumbers.length < selectedRows.length 
                    ? `${trackingNumbers.join('_')}_and_${selectedRows.length - trackingNumbers.length}_more`
                    : trackingNumbers.join('_');
                const filename = `shipping_labels_${filenameSuffix}.pdf`;
                
                printPdfFromBase64(data.generateBatchShippingLabelPdf, filename);
                message.success(t('wms.parcels.messages.generatedBatchShippingLabel', { count: selectedRows.length }));
            }
        } catch (error: any) {
            console.error('Print tag failed:', error);
            const graphQLError = error?.graphQLErrors?.[0];
            const messageKey = graphQLError?.extensions?.messageKey;

            if (messageKey === 'errors.business.fbt.sortingOnlyBatchLabelNotSupported') {
                // 针对仅分拣订单错误，格式化 trackingNumbers
                const details = graphQLError.extensions?.details;
                const trackingNumbers = details?.trackingNumbers;
                const formattedDetails = trackingNumbers && Array.isArray(trackingNumbers)
                    ? { ...details, trackingNumbers: trackingNumbers.join(', ') }
                    : details;

                // 使用格式化后的 details 进行翻译并显示
                const errorMessage = String(t(messageKey, formattedDetails));
                message.error(errorMessage);
            } else if (messageKey) {
                // 其他有 messageKey 的错误，使用 showError
                showError(error);
            } else {
                // 兜底：无 messageKey 时使用通用文案
                message.error(t('wms.parcels.messages.failedToGenerateBatchLabels'));
            }
        }
    };

    const handleBatchMarkOk = async (orders: BatchMarkExceptionInput[]) => {
        try {
            setBatchMarkLoading(true);

            await Promise.all(orders.map(order =>
                markAsException({
                    variables: {
                        orderId: order.orderId,
                        exceptionReasonCode: order.exceptionReasonCode,
                        exceptionImageS3KeysJson: order.exceptionImageS3KeysJson || [],
                        exceptionHubId: siteType === SiteType.HUB ? currentWarehouse?.id : currentWarehouse?.parentSiteId,
                        exceptionWarehouseId: siteType === SiteType.WAREHOUSE ? currentWarehouse?.id : null
                    },
                    refetchQueries: ['WarehouseQuery']
                })
            ))
            message.success(t('wms.parcels.messages.markingExceptionSuccessfully'));
            setBatchModalVisible(false);
            handleFilter();
        } catch (error) {
            console.error('Marking exception failed:', error);
             message.error(t('wms.parcels.messages.markingExceptionFailed'));
        } finally {
            setBatchMarkLoading(false);
        }
    };

    const handleSetReturnStatus = () => {
        if (selectedRows.length < 1) {
            message.warning(t('wms.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        // 检查是否所有选中的包裹都是退件相关状态
        if (!selectedRows.every( row =>
            row.overallStatus === 'RETURN_ARRIVED_DESTINATION_HUB' ||
            row.overallStatus === 'RETURN_STARTED'
        )){
           message.warning(t('wms.parcels.messages.selectParcelsWithReturnDirection'));
            return;
        }
        setReturnStatusVisible(true);
    };

    // 添加到POD人审队列处理函数
    const handleAddToPodReview = async () => {
        // 防重复点击 - 放在最前面
        if (addToPodReviewLoading) {
            return;
        }

        // 立即设置loading状态，防止重复点击
        setAddToPodReviewLoading(true);

        if (selectedRows.length < 1) {
            message.warning(t('podReview.addToPodReview.selectAtLeastOneParcel'));
            // 短暂延迟后重置loading状态
            setTimeout(() => setAddToPodReviewLoading(false), 1000);
            return;
        }

        // 检查运单状态，只有妥投状态才能添加到人审
        const nonDeliveredOrders = selectedRows.filter(row => row.overallStatus !== 'DELIVERED');
        if (nonDeliveredOrders.length > 0) {
            message.warning(t('podReview.messages.onlyDeliveredOrdersCanAddToPodReview'));
            // 短暂延迟后重置loading状态
            setTimeout(() => setAddToPodReviewLoading(false), 1000);
            return;
        }
        try {
            // 获取选中行的订单ID
            const orderIds = selectedRows.map(row => parseInt(row.id)).filter(id => !isNaN(id));

            if (orderIds.length === 0) {
                message.warning(t('podReview.addToPodReview.noValidOrderIds'));
                return;
            }

            // 调用后端API标记为待人工审核
            const {data} = await batchMarkForManualReview({
                variables: {
                    orderIds: orderIds
                },
                refetchQueries: ['WmsParcelsQuery']
            });

            if (data?.batchMarkForManualReview) {
                const result = data.batchMarkForManualReview;
                setAddToPodReviewResult({batchMarkForManualReview: result});
                setAddToPodReviewModalVisible(true);
            } else {
                message.error(t('podReview.addToPodReview.failedToAdd'));
            }
        } catch (error: any) {
            console.error('Add to POD review error:', error);
            message.error(t('podReview.addToPodReview.failedToAdd'));
        } finally {
            setAddToPodReviewLoading(false);
        }
    };

    const handleSetReturnStatusConfirm = async (formData: SetReturnStatusData) => {
        try {
            setReturnStatusLoading(true);
            await setReturnedStatus({
                variables: {
                    orderIds: formData.orderIds,
                    returnedImageS3KeysJson: formData.returnedImageS3KeysJson
                }
            })
            message.success(t('wms.parcels.messages.setReturnStatusSuccessfully'));
            setReturnStatusVisible(false);
            handleFilter();
        } catch (error) {
            console.error('set return status failed:', error);
            const { message: errorMessage } = parseException(error);
            message.error(errorMessage);
        } finally {
            setReturnStatusLoading(false);
        }
    };

    // 下载包裹报表
    const handleDownloadParcels = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        // 检查冷却时间
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again to avoid duplicate requests`);
            return;
        }

        // 检查是否正在生成
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime); // 存储到 localStorage

            // 获取用户时区
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // 构建查询过滤器：与「筛选」入口共用同一份逻辑，先按页面默认值补齐 orderTime，
            // 再经 normalizeRequiredFields 注入仓库隔离锚点（currentWarehouseId/sortingCode），
            // 确保下载与筛选发往后端的过滤条件完全一致
            const withDefaults = { ...filters, orderTime: getEffectiveOrderTime(filters.orderTime) };
            const queryFilters = buildParcelQueryFilters(
                normalizeRequiredFields(withDefaults, currentWarehouse),
                currentWarehouse
            );

            // 如果有选中的行，只下载选中的数据
            if (selectedRows && selectedRows.length > 0) {
                const trackingNumbers = selectedRows
                    .map(row => row.trackingNumber)
                    .filter((tn): tn is string => !!tn);

                if (trackingNumbers.length > 0) {
                    // 使用选中的 trackingNumbers 覆盖过滤条件
                    queryFilters.trackingNumbers = trackingNumbers.join(';');
                }
            }

            const response = await generateReportMutation({
                variables: {
                    filters: queryFilters,
                    timezone: userTimezone,
                    siteType: String(siteType || 'HUB'),
                }
            });

            const fileName = response.data?.generateParcelDownloadReportForWMS;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error(t('errors.system.internal'));
            }
        } catch (error: any) {
            console.error('Report generation failed:', error);
            showError(error);
            setDownloadLoading(false);
        }
    };

    return (
        <div className="mb-2 pl-0 py-2 [&_.ant-select]:h-8 [&_.ant-select-selector]:h-8 [&_.ant-input:not(textarea)]:h-8 [&_.ant-picker]:h-8">
            <OrderListingQueryHint />
            {/* Base Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-800 font-semibold mb-2">{t('filters.sections.baseInformation')}</div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐（Tracking 占满一行，依赖 gap-y 保持与下一行一致的间距） */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0 col-span-3">
                            <BatchInputFilterItem
                                label={labelTracking}
                                value={filters.trackingNumbers}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                                    ...filters,
                                    trackingNumbers: e.target.value || undefined
                                })}
                                placeholder={t('wms.parcels.placeholders.trackingNumbers') || ''}
                                spanClassName={col1SpanClassName}
                                rows={2}
                            />
                        </div>
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelOrder}
                    value={filters.channelOrderNumber}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({...filters, channelOrderNumber: e.target.value || undefined})}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelExternalTracking}
                    value={filters.externalTrackingNumber}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({...filters, externalTrackingNumber: e.target.value || undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelSortingChannelOrderNumber}
                    value={filters.sortingChannelOrderNumber}
                    onChange={(e) => setFilters({...filters, sortingChannelOrderNumber: e.target.value || undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                        {/* 第二行：DSP | 客户 | 运单状态 */}
                        <div className="min-w-0">
                <DspSelectFilter
                    label={labelDsp}
                    value={filters.deliveredDspIds?.map(String)}
                    onChange={(value: string[] | undefined) => setFilters({...filters, deliveredDspIds: value?.map(v => parseInt(v)), deliveredDspId: undefined})}
                    spanClassName={col1SpanClassName}
                    placeholder={t('wms.parcels.filters.all') as string}
                    warehouseId={siteType === SiteType.WAREHOUSE && currentWarehouse?.id ? Number(currentWarehouse.id) : undefined}
                    hubId={siteType === SiteType.HUB && currentWarehouse?.id ? Number(currentWarehouse.id) : undefined}
                />
                        </div>
                        <div className="min-w-0">
                <MultiClientSelectFilter
                    label={labelClient}
                    value={filters.channelId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, channelId: value})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    placeholder={t('wms.parcels.filters.all') as string}
                />
                        </div>
                        <div className="min-w-0">
                <MultiParcelStatusSelectFilter
                    label={labelParcelStatus}
                    value={filters.overallStatus}
                    onChange={(value: string[] | undefined) => setFilters({...filters, overallStatus: value})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                    placeholder={t('wms.parcels.filters.all') as string}
                />
                        </div>
                </div>
                </div>
            </div>

            {/* Route Information */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.routeInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <HubSelectFilter
                    label={labelFirstSortCode}
                    value={filters.sortingCodeSegment1}
                    onChange={(value) => {
                        // value 是 firstSegment（一段码），需要设置到 sortingCodeSegment1
                        if (!value) {
                            // 当清空 Hub 选择时，同时清空相关字段
                            setFilters({
                                ...filters,
                                sortingCodeSegment1: undefined,
                                hubId: undefined,
                                warehouseId: undefined,
                                sortingCodeSegment2: undefined // 清空一段码时，同时清空二段码
                            });
                            setSelectedHubId(undefined);
                        } else {
                            // 选择 Hub 时，设置 sortingCodeSegment1，但不设置 hubId
                            // 因为使用分拣码段查询可以支持查询新建订单（dest_warehouse_id 为 NULL 的订单）
                            setFilters({
                                ...filters,
                                sortingCodeSegment1: value,
                                hubId: undefined, // 不设置 hubId，只使用分拣码段查询
                                warehouseId: undefined, // 不设置 warehouseId，只使用分拣码段查询
                                sortingCodeSegment2: undefined // 清空二段码
                            });
                        }
                    }}
                    onSelectHub={(hubId) => {
                        setSelectedHubId(hubId); // 保存选中的 Hub ID，用于级联过滤（但不用于查询）
                        // 注意：当用户手动选择分拣码段时，不设置 hubId，只使用分拣码段查询
                        // 这样可以支持查询新建订单（dest_warehouse_id 为 NULL 的订单）
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('wms.parcels.placeholders.selectHub') || 'Select HUB'}
                />
                        </div>
                        <div className="min-w-0">
                <WarehouseSelectFilter
                    label={labelSecondSortCode}
                    value={filters.warehouseId}
                    onChange={handleWarehouseIdChange}
                    onSelectSortingName={handleSortingNameChange}
                    onSelectSecondSegment={handleSecondSegmentChange}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    hubId={selectedHubId} // 使用 hubId 进行级联过滤（基于 parent_site_id）
                    placeholder={t('wms.parcels.placeholders.selectWarehouse') || 'Select Warehouse'}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelThirdSortCode}
                    value={filters.sortingCodeSegment3}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({...filters, sortingCodeSegment3: e.target.value || undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                    disabled={sortingCodes[2] !== undefined}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelReceiverZipcode}
                    value={filters.receiverZipcodeInput || filters.receiverZipcode?.join('\n') || ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        const value = e.target.value;
                        // 与 trackingNumbers 一致：直接保存原始字符串，在查询时转换为数组
                        setFilters({
                            ...filters,
                            receiverZipcodeInput: value || undefined
                        });
                    }}
                    placeholder={t('wms.parcels.placeholders.receiverZipcode') || 'Enter multiple zipcodes, separated by line, comma or space'}
                    spanClassName={col1SpanClassName}
                    rows={1}
                  />
                        </div>
                        <div className="min-w-0">
                <BagTaskNumberFilterItem
                    label={labelBagTaskNumber}
                    placeholder={t('wms.parcels.placeholders.bagTaskNumber') || ''}
                    value={filters.bagTaskNumbers}
                    onChange={(value: string | undefined) => setFilters({
                        ...filters,
                        bagTaskNumbers: value
                    })}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0">
                <MultiPickupHubSelectFilter
                    label={labelPickupHub}
                    value={filters.pickupHubId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, pickupHubId: value})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                    placeholder={t('wms.parcels.filters.all') as string}
                />
                        </div>
                        {/* 第三行 */}
                        <div className="min-w-0">
                <PrePickupHubSelectFilter
                    label={labelPrePickupHub}
                    value={filters.prePickupHubName}
                    onChange={(name: string | undefined) => {
                        setFilters({ ...filters, prePickupHubName: name });
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('central.parcels.placeholders.prePickupHub') || ''}
                />
                </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelPickupSite}
                    value={
                        filters.cooperationWarehouseId !== undefined && filters.cooperationWarehouseId !== null
                            ? filters.cooperationWarehouseId.toString()
                            : ''
                    }
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const rawValue = e.target.value.trim();
                        if (rawValue === '') {
                            setFilters({ ...filters, cooperationWarehouseId: undefined });
                            return;
                        }
                        const numericValue = Number(rawValue);
                        if (!Number.isNaN(numericValue)) {
                            setFilters({ ...filters, cooperationWarehouseId: numericValue });
                        }
                    }}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    inputClassName="h-8"
                    inputStyle={{ paddingTop: 0, paddingBottom: 0, lineHeight: '32px' }}
                />
                </div>
                        <div className="min-w-0"></div>
                    </div>
                </div>
            </div>

            {/* Date Information */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.dateInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐 - 二等分布局 */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelOrderTime}
                    value={getEffectiveOrderTime(filters.orderTime)}
                    onChange={handleOrderTimeChange}
                    spanClassName={col1SpanClassName}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelPickupTaskDate}
                    value={filters.receivedAtHub}
                    onChange={handlePickupAtChange}
                    spanClassName={getSmartLabelClassName(dateCol2Labels)}
                                        format="YYYY/MM/DD HH:mm"
                                        showTime={{
                                            format: 'HH:mm',
                                            defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                        }}
                                    />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                            <DeliveredAtRangeField
                                label={labelDeliveredAt}
                                value={filters.deliveredAt}
                                onChange={handleDeliveredAtChange}
                                spanClassName={col1SpanClassName}
                                days={queryRangeDays}
                                system="WMS"
                                filters={filters}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelHubReceiveDate}
                                value={filters.destHubArrivedAt}
                                onChange={handleHubReceiveChange}
                                spanClassName={getSmartLabelClassName(dateCol2Labels)}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                            />
                        </div>
                        {/* 第三行 */}
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelWhsReceiveDate}
                    value={filters.destWarehouseArrivedAt}
                    onChange={handleWarehouseReceiveChange}
                    spanClassName={col1SpanClassName}
                                    format="YYYY/MM/DD HH:mm"
                                    showTime={{
                                        format: 'HH:mm',
                                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                    }}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastDeliveryScanTime}
                    value={filters.lastDeliveryScanTime}
                    onChange={handleLastDeliveryScanTimeChange}
                    spanClassName={getSmartLabelClassName(dateCol2Labels)}
                                    format="YYYY/MM/DD HH:mm"
                                    showTime={{
                                        format: 'HH:mm',
                                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                    }}
                                />
                        </div>
                        {/* 第四行 */}
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastTaskDate}
                    value={filters.deliverAt}
                    onChange={handleDeliverTaskChange}
                    spanClassName={col1SpanClassName}
                                    format="YYYY/MM/DD HH:mm"
                                    showTime={{
                                        format: 'HH:mm',
                                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                    }}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastUpdatedAt}
                    value={filters.updatedAt}
                    onChange={handleUpdatedAtChange}
                    spanClassName={getSmartLabelClassName(dateCol2Labels)}
                                    format="YYYY/MM/DD HH:mm"
                                    showTime={{
                                        format: 'HH:mm',
                                        defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                    }}
                />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelReturnArrivalHubAt}
                                value={filters.returnArrivalHubAt}
                                onChange={handleReturnArrivalHubAtChange}
                                spanClassName={col1SpanClassName}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Exception Information */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.exceptionInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelIsComplaint}
                    options={isComplaintOptions}
                    value={filters.isComplaint?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, isComplaint: value ? (value === 'true') : undefined})}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelPodReviewStatus}
                    options={podReviewStatusOptions}
                    value={filters.podReviewStatus?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, podReviewStatus: value || undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelHasException}
                    options={hasExceptionOptions}
                    value={filters.isHasBeenSetException?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, isHasBeenSetException: value ? (value === 'true') : undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelLastExceptionReason}
                    options={exceptionList.map(ex => ({
                        value: ex.exceptionReasonCode,
                        label: getExceptionReasonText(ex, ex.exceptionReasonCode)
                    })) || []}
                    value={filters.exceptionReasonCode ?? undefined}
                    onChange={(value: string) => setFilters({...filters, exceptionReasonCode: value || undefined})}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelExceptionStatus}
                    options={exceptionOrderStatusOptions}
                    mode="multiple"
                    maxTagCount="responsive"
                    value={filters?.exceptionStatuses?.map(String) ?? []}
                    onChange={(values: string[]) => setFilters({...filters, exceptionStatuses: values && values.length > 0 && values.length < exceptionOrderStatusOptions.length ? values.map(Number) : undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelNextAction}
                    options={nextActionSelectData.filter(item => item.value !== 0).map(item => ({
                        value: item.name,
                        label: getNextActionTextFromBackend(item.name)
                    }))}
                    mode="multiple"
                    value={filters.nextActions}
                    onChange={(value: string[] | undefined) => setFilters({...filters, nextActions: value, nextAction: undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                    </div>

                    {/* 第三行（二分）：上次异常时间 */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start mt-4">
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelLastExceptionTime}
                                value={filters.markExceptionAt}
                                onChange={handleExceptionTimeChange}
                                spanClassName={col1SpanClassName}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                            />
                        </div>
                        <div className="min-w-0"></div>
                    </div>
                </div>
            </div>

            <div className="mb-4">
                {isSpanish ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-2">
                        <div className="flex flex-wrap gap-2">
                            <Button 
                                type="primary" 
                                onClick={handlePrintLabel}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.buttons.printLabel')}</span>
                            </Button>
                            <Button 
                                type="primary" 
                                onClick={handleMarkAsException}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.buttons.markAsException')}</span>
                            </Button>
                            <Button 
                                type="primary" 
                                onClick={handleSetReturnStatus}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.buttons.setReturnStatus')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleAddToPodReview}
                                loading={addToPodReviewLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('podReview.addToPodReview.title')}</span>
                            </Button>
                            {hasPodUpdatePermission && (
                                <Button 
                                    type="primary" 
                                    onClick={() => handleSetDeliverStatus(selectedRows)}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('wms.parcels.buttons.setDeliverStatus')}</span>
                                </Button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 ml-auto">
                            <Button
                                type="primary"
                                onClick={copyTrackingNumbers}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleDownloadParcels}
                                loading={cooldownRemaining > 0}
                                disabled={downloadLoading || cooldownRemaining > 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `Download ${cooldownRemaining}s`
                                    : t('components.button.download')
                                }
                                </span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={guardedHandleFilter}
                                disabled={deliveredAtBlocked}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.filters.filter')}</span>
                            </Button>
                            <Button
                                onClick={handleResetFiltersWithDefaultTime}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.filters.reset')}</span>
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-x-2 gap-y-4">
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="primary"
                                onClick={handlePrintLabel}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.buttons.printLabel')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleMarkAsException}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.buttons.markAsException')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleSetReturnStatus}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.buttons.setReturnStatus')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleAddToPodReview}
                                loading={addToPodReviewLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('podReview.addToPodReview.title')}</span>
                            </Button>
                            {hasPodUpdatePermission && (
                                <Button
                                    type="primary"
                                    onClick={() => handleSetDeliverStatus(selectedRows)}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('wms.parcels.buttons.setDeliverStatus')}</span>
                                </Button>
                            )}
                        </div>

                        <div className="flex flex-wrap gap-2 ml-auto">
                            <Button
                                type="primary"
                                onClick={copyTrackingNumbers}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleDownloadParcels}
                                loading={cooldownRemaining > 0}
                                disabled={downloadLoading || cooldownRemaining > 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `Download ${cooldownRemaining}s`
                                    : t('components.button.download')
                                }
                                </span>
                            </Button>
                            <Button
                                type="primary"
                                onClick={guardedHandleFilter}
                                disabled={deliveredAtBlocked}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.filters.filter')}</span>
                            </Button>
                            <Button
                                onClick={handleResetFiltersWithDefaultTime}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('wms.parcels.filters.reset')}</span>
                            </Button>
                        </div>
                    </div>
                )}
            </div>

             {/* 批量标记异常弹窗 */}
             <BatchMarkAsExceptionModal
                    visible={batchModalVisible}
                    parcels={selectedRows.map(row => ({
                        orderId: Number(row.id),
                        trackingNumber: row.trackingNumber,
                        existingImageUrls: row.exceptionImageS3UrlsJson || [],
                        exceptionHubId: row.exceptionHubId,
                        exceptionWarehouseId: row.exceptionWarehouseId
                    }))}
                    onOk={handleBatchMarkOk}
                    onCancel={() => setBatchModalVisible(false)}
                    loading={batchMarkLoading}
                    userRole={userRole}
                    exceptionSelectData={exceptionSelectData}
                />



            <SetReturnStatusModal
                visible={returnStatusVisible}
                onClose={() => setReturnStatusVisible(false)}
                onConfirm={handleSetReturnStatusConfirm}
                loading={returnStatusLoading}
                parcels={selectedRows}
            />

            {/* 下载完成弹窗 */}
            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />

            <AddToPodReviewModal
                visible={addToPodReviewModalVisible}
                onClose={() => setAddToPodReviewModalVisible(false)}
                result={addToPodReviewResult?.batchMarkForManualReview || null}
            />

            <SetDeliverStatusModal
                visible={deliverStatusVisible}
                onClose={() => setDeliverStatusVisible(false)}
                onConfirm={handleSetDeliverStatusConfirm}
                loading={deliverStatusLoading}
                parcels={filteredDeliverRows}
            />
            <FailedOrdersModal
                visible={failedOrdersVisible}
                failedOrders={failedOrders}
                onClose={() => {
                    setFailedOrdersVisible(false);
                    setFailedOrders([]);
                }}
            />
        </div>
    );
};

export default WmsParcelFilter;